import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  useCreateDeviceModel,
  useCreateDeviceType,
  useDeleteDeviceModel,
  useDeleteDeviceType,
  useDeviceTypes,
  useUpdateDeviceModel,
  useUpdateDeviceType
} from '../../api/queries'
import type { DeviceModelOption, DeviceTypeOption } from '../../api/types'
import { Card, EmptyState, ErrorBox, Loading } from '../../components/ui'

/**
 * Enhedstyper og modeller - den faste liste (beslutning 105).
 *
 * <b>Omdøbes en type, rettes den på alle enheder, checklister og manualer</b>, så checklisten bliver
 * ved med at blive foreslået. En type eller model i brug kan ikke slettes - den slås fra i stedet:
 * så kan den ikke vælges mere, men bliver stående, hvor den er brugt.
 */
export function DeviceTypesPage() {
  const { data, isLoading, error } = useDeviceTypes(true)
  const createType = useCreateDeviceType()
  const updateType = useUpdateDeviceType()
  const removeType = useDeleteDeviceType()

  const [name, setName] = useState('')
  const [problem, setProblem] = useState<string | null>(null)

  async function attempt(action: () => Promise<unknown>, fallback: string) {
    setProblem(null)
    try {
      await action()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : fallback)
    }
  }

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  const list = [...(data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'da'))

  async function move(type: DeviceTypeOption, direction: -1 | 1) {
    const index = list.findIndex((t) => t.id === type.id)
    const neighbour = list[index + direction]
    if (!neighbour) return
    // Står de ens, gives de hver sit tal, så byttet kan ses.
    const mine = neighbour.sortOrder === type.sortOrder ? type.sortOrder + direction : neighbour.sortOrder
    await attempt(async () => {
      await updateType.mutateAsync({ id: type.id, name: type.name, sortOrder: mine })
      await updateType.mutateAsync({ id: neighbour.id, name: neighbour.name, sortOrder: type.sortOrder })
    }, 'Rækkefølgen kunne ikke gemmes.')
  }

  function rename(type: DeviceTypeOption) {
    const next = prompt(
      `Nyt navn til "${type.name}". Det rettes også på ${type.deviceCount} enhed(er), checklister og manualer.`,
      type.name
    )
    if (!next || next.trim() === type.name) return
    void attempt(() => updateType.mutateAsync({ id: type.id, name: next.trim() }), 'Typen kunne ikke omdøbes.')
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Card title="Enhedstyper og modeller">
        <p className="small muted" style={{ marginTop: 0 }}>
          Typen og modellen på en <Link to="/devices">enhed</Link> vælges fra denne liste, så de staves ens overalt -
          det er typen, der afgør, hvilken servicechecklist og hvilke manualer der foreslås. Projektledere kan tilføje
          nye direkte fra enhedsformularen; her rettes, slås fra og slettes.
        </p>

        <form
          className="row"
          style={{ gap: 8, flexWrap: 'wrap' }}
          onSubmit={(e) => {
            e.preventDefault()
            if (!name.trim()) return
            void attempt(async () => {
              await createType.mutateAsync(name.trim())
              setName('')
            }, 'Typen kunne ikke gemmes.')
          }}
        >
          <input
            value={name}
            placeholder="Ny type, f.eks. Nødbelysning"
            maxLength={100}
            style={{ flex: 1, minWidth: 180 }}
            onChange={(e) => setName(e.target.value)}
          />
          <button className="btn btn--primary btn--sm" type="submit" disabled={createType.isPending || !name.trim()}>
            Tilføj type
          </button>
        </form>

        {problem && <ErrorBox error={problem} />}
      </Card>

      {list.length === 0 && (
        <Card>
          <EmptyState>Der er ingen typer endnu. Skriv den første i feltet ovenfor.</EmptyState>
        </Card>
      )}

      {list.map((type, index) => (
        <Card key={type.id}>
          <div className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap', opacity: type.isActive ? 1 : 0.6 }}>
            <div style={{ flex: 1, minWidth: 160 }}>
              <div style={{ fontWeight: 700 }}>
                {type.name}
                {!type.isActive && <span className="tiny muted"> · slået fra</span>}
              </div>
              <div className="tiny muted">
                {type.deviceCount === 0 ? 'Ingen enheder' : `${type.deviceCount} ${type.deviceCount === 1 ? 'enhed' : 'enheder'}`}
                {' · '}
                {type.models.length} {type.models.length === 1 ? 'model' : 'modeller'}
              </div>
            </div>

            <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
              <button className="btn btn--sm" disabled={index === 0} onClick={() => void move(type, -1)} aria-label="Flyt op">
                ↑
              </button>
              <button
                className="btn btn--sm"
                disabled={index === list.length - 1}
                onClick={() => void move(type, 1)}
                aria-label="Flyt ned"
              >
                ↓
              </button>
              <button className="btn btn--sm" onClick={() => rename(type)}>
                Omdøb
              </button>
              <button
                className="btn btn--sm"
                onClick={() =>
                  void attempt(
                    () => updateType.mutateAsync({ id: type.id, name: type.name, isActive: !type.isActive }),
                    'Typen kunne ikke gemmes.'
                  )
                }
              >
                {type.isActive ? 'Slå fra' : 'Slå til'}
              </button>
              <button
                className="btn btn--danger btn--sm"
                disabled={type.deviceCount > 0}
                title={type.deviceCount > 0 ? 'Typen bruges af enheder. Slå den fra i stedet.' : undefined}
                onClick={() => {
                  if (!confirm(`Slet typen "${type.name}" og dens modeller?`)) return
                  void attempt(() => removeType.mutateAsync(type.id), 'Typen kunne ikke slettes.')
                }}
              >
                Slet
              </button>
            </div>
          </div>

          <ModelList type={type} onProblem={setProblem} />
        </Card>
      ))}
    </div>
  )
}

function ModelList({ type, onProblem }: { type: DeviceTypeOption; onProblem: (text: string | null) => void }) {
  const createModel = useCreateDeviceModel()
  const updateModel = useUpdateDeviceModel()
  const removeModel = useDeleteDeviceModel()
  const [name, setName] = useState('')
  const [manufacturer, setManufacturer] = useState('')

  async function attempt(action: () => Promise<unknown>, fallback: string) {
    onProblem(null)
    try {
      await action()
    } catch (err) {
      onProblem(err instanceof Error ? err.message : fallback)
    }
  }

  function edit(model: DeviceModelOption) {
    const nextName = prompt('Modellens navn', model.name)
    if (nextName === null || !nextName.trim()) return
    const nextManufacturer = prompt('Fabrikat (tom = intet)', model.manufacturer ?? '')
    if (nextManufacturer === null) return
    void attempt(
      () =>
        updateModel.mutateAsync({
          id: model.id,
          name: nextName.trim(),
          manufacturer: nextManufacturer.trim() || null
        }),
      'Modellen kunne ikke gemmes.'
    )
  }

  return (
    <div className="stack" style={{ gap: 6, marginTop: 10, paddingLeft: 12, borderLeft: '2px solid var(--border, #E2E8F0)' }}>
      {type.models.length === 0 && <div className="tiny muted">Ingen modeller endnu.</div>}

      {type.models.map((m) => (
        <div key={m.id} className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap', opacity: m.isActive ? 1 : 0.6 }}>
          <div style={{ flex: 1, minWidth: 140 }}>
            <span className="small">{m.name}</span>
            {m.manufacturer && <span className="tiny muted"> · {m.manufacturer}</span>}
            {!m.isActive && <span className="tiny muted"> · slået fra</span>}
            {m.deviceCount > 0 && (
              <span className="tiny muted">
                {' '}
                · {m.deviceCount} {m.deviceCount === 1 ? 'enhed' : 'enheder'}
              </span>
            )}
          </div>
          <div className="row" style={{ gap: 4 }}>
            <button className="btn btn--sm" onClick={() => edit(m)}>
              Ret
            </button>
            <button
              className="btn btn--sm"
              onClick={() =>
                void attempt(
                  () => updateModel.mutateAsync({ id: m.id, name: m.name, manufacturer: m.manufacturer ?? null, isActive: !m.isActive }),
                  'Modellen kunne ikke gemmes.'
                )
              }
            >
              {m.isActive ? 'Slå fra' : 'Slå til'}
            </button>
            <button
              className="btn btn--danger btn--sm"
              disabled={m.deviceCount > 0}
              title={m.deviceCount > 0 ? 'Modellen bruges af enheder. Slå den fra i stedet.' : undefined}
              onClick={() => {
                if (!confirm(`Slet modellen "${m.name}"?`)) return
                void attempt(() => removeModel.mutateAsync(m.id), 'Modellen kunne ikke slettes.')
              }}
            >
              Slet
            </button>
          </div>
        </div>
      ))}

      {type.isActive && (
        <form
          className="row"
          style={{ gap: 6, flexWrap: 'wrap' }}
          onSubmit={(e) => {
            e.preventDefault()
            if (!name.trim()) return
            void attempt(async () => {
              await createModel.mutateAsync({ typeId: type.id, name: name.trim(), manufacturer: manufacturer.trim() || null })
              setName('')
              setManufacturer('')
            }, 'Modellen kunne ikke gemmes.')
          }}
        >
          <input
            value={name}
            placeholder="Ny model"
            maxLength={100}
            style={{ flex: 1, minWidth: 120 }}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            value={manufacturer}
            placeholder="Fabrikat (valgfri)"
            maxLength={100}
            style={{ flex: 1, minWidth: 120 }}
            onChange={(e) => setManufacturer(e.target.value)}
          />
          <button className="btn btn--sm" type="submit" disabled={createModel.isPending || !name.trim()}>
            Tilføj model
          </button>
        </form>
      )}
    </div>
  )
}
